import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Play, Filter } from "lucide-react";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(searchParams.get("category") || "all");

  const categories = [
    { id: "all", label: "All Work" },
    { id: "staff-engagement", label: "Staff Engagement" },
    { id: "training", label: "Training" },
    { id: "health_safety", label: "Health & Safety" },
    { id: "recruitment", label: "Recruitment" },
  ];

  useEffect(() => {
    fetchCaseStudies();
  }, [activeFilter]);

  const fetchCaseStudies = async () => {
    setLoading(true);
    try {
      const url = activeFilter === "all" 
        ? `${API}/case-studies` 
        : `${API}/case-studies?category=${activeFilter}`;
      const response = await axios.get(url);
      setCaseStudies(response.data);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (categoryId) => {
    setActiveFilter(categoryId);
    if (categoryId === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "values": return "#00E5FF";
      case "training": return "#E50914";
      case "health_safety": return "#f97316";
      case "recruitment": return "#a855f7";
      default: return "#00E5FF";
    }
  };

  return (
    <div data-testid="case-studies-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Internal Communications Case Studies & Portfolio"
        description="Explore our portfolio of internal communications projects for large organisations. Case studies featuring health and safety training, employee engagement campaigns, recruitment marketing and organisational change communications."
        keywords="internal communications case studies, internal comms portfolio, employee engagement projects, health and safety training case study, recruitment marketing examples, large organisation communications"
        canonical="/case-studies"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-6 md:pt-40 md:pb-8" data-testid="case-studies-hero">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: "radial-gradient(ellipse at top right, rgba(0, 229, 255, 0.15) 0%, transparent 50%)"
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              OUR WORK
            </h1>
            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl">
              Real results for real businesses. Explore how we've transformed 
              internal communications across industries with strategic thinking 
              and creative execution.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#1F1F1F]" data-testid="case-studies-filters">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide">
            <Filter className="w-5 h-5 text-[#B3B3B3] mr-2 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category.id}
                data-testid={`filter-${category.id}`}
                onClick={() => handleFilterChange(category.id)}
                className={`px-4 py-2 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${
                  activeFilter === category.id
                    ? "bg-[#00E5FF] text-black"
                    : "bg-[#1F1F1F] text-white hover:bg-[#333]"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-24" data-testid="case-studies-grid">
        <div className="container mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : caseStudies.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#B3B3B3] text-lg">No case studies found for this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudies.map((study) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.id}`}
                  data-testid={`case-study-card-${study.id}`}
                  className="group"
                >
                  <div className="relative aspect-video bg-[#141414] rounded-lg overflow-hidden netflix-card border border-transparent">
                    <img
                      src={study.thumbnail}
                      alt={study.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span 
                        className="px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full border"
                        style={{
                          backgroundColor: `${getCategoryColor(study.category)}20`,
                          color: getCategoryColor(study.category),
                          borderColor: `${getCategoryColor(study.category)}30`
                        }}
                      >
                        {study.category?.replace("_", " ")}
                      </span>
                    </div>

                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-[#00E5FF] flex items-center justify-center">
                        <Play className="w-7 h-7 text-black fill-black" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-[#B3B3B3] text-xs uppercase tracking-wider">
                        {study.client} • {study.industry}
                      </span>
                      <h3 className="font-display text-lg md:text-xl text-white tracking-wide mt-1">
                        {study.title}
                      </h3>
                    </div>
                  </div>

                  {/* Hover Expanded Content */}
                  <div className="mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[#B3B3B3] text-sm line-clamp-2">{study.summary}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {study.services_used?.slice(0, 3).map((service, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#B3B3B3] rounded"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#0a0a0a]" data-testid="case-studies-cta">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              READY TO CREATE YOUR
              <br />
              <span className="text-[#00E5FF]">SUCCESS STORY?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help transform your internal communications 
              and deliver results like these.
            </p>
            <Link to="/contact">
              <Button
                data-testid="case-studies-cta-btn"
                className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-10 py-6 uppercase tracking-wider text-base"
              >
                Start Your Project
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
